<template>
    <div
        :class="{
            'main-layout--white-bg': whiteBg,
            'layout--loading': logoutInProgress,
            [`lang-${$i18n.locale}`]: true,
        }"
        class="main-layout"
    >
        <LoadingIndicator v-if="logoutInProgress" />
        <template v-else>
            <MainHeaderBanner v-if="displayHeaderWithinErrorPage" />

            <div class="main-layout__content-wrapper">
                <MainHeader v-if="displayHeaderWithinErrorPage" class="main-layout__header" />
                <div class="main-layout__content">
                    <Nuxt />
                </div>
                <MainFooter v-if="displayFooter" class="main-layout__footer" />
                <Modal v-if="modalConfig !== null" :config="modalConfig" />
                <SlideUpModal v-if="slideUpModalConfig !== null && !isLargeScreen" :config="slideUpModalConfig" />
                <template v-if="bannerDelayPassed">
                    <FloatingBanner v-show="shouldShowOwnerCampaignBanner" closeable-id="owner_campaign">
                        <span>
                            {{ $t('banners.owner_campaign.text') }}
                            <LocalizedLink :to="{ name: 'sign-up' }" target="_blank">
                                {{ $t('banners.owner_campaign.link_text') }}
                            </LocalizedLink>
                        </span>
                    </FloatingBanner>
                    <FloatingBanner v-show="shouldShowRenterCampaignBanner" closeable-id="renter_campaign">
                        <span>
                            {{ $t('banners.renter_campaign.text') }}
                            <a
                                href="https://help.mycamper.com/en/articles/362282-terms-and-conditions-mycamper-book-and-win-competition"
                                target="_blank"
                                >{{ $t('banners.renter_campaign.link_text') }}
                            </a>
                        </span>
                    </FloatingBanner>
                </template>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MainHeaderBanner from '~/components/layout/MainHeaderBanner.vue';
import MainHeader from '~/components/layout/MainHeader.vue';
import MainFooter from '~/components/layout/MainFooter.vue';
import Modal from '~/components/modals/Modal.vue';
import { actionTypes as configActions } from '~/store/config';
import { actionTypes as navigationActions } from '~/store/navigation';
import userModalsHandlerMixin from '~/mixins/userModalsHandlerMixin';
import logoutRedirectMixin from '~/mixins/logoutRedirectMixin';
import SlideUpModal from '~/components/modals/SlideUpModal';
import posthogMixin from '~/mixins/posthogMixin';
import intercomMixin from '~/mixins/intercomMixin';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import FloatingBanner from '~/components/FloatingBanner.vue';

export default {
    name: 'DefaultLayout',
    components: {
        SlideUpModal,
        MainHeaderBanner,
        MainHeader,
        MainFooter,
        Modal,
        LoadingIndicator,
        FloatingBanner,
    },
    mixins: [userModalsHandlerMixin, logoutRedirectMixin, posthogMixin, intercomMixin],
    middleware: ['isMobileOrLarge'],
    data() {
        return {
            bannerDelayPassed: false,
        };
    },
    async fetch() {
        try {
            await this.$store.dispatch(configActions.FETCH_RATES);
        } catch (err) {
            this.$sentry.captureException(err);
        }
    },
    computed: {
        ...mapGetters([
            'modalConfig',
            'slideUpModalConfig',
            'isLargeScreen',
            'logoutInProgress',
            'isLoggedIn',
            'featureFlagsLoaded',
            'isFeatureFlagLoaded',
            'isOwner',
        ]),
        floatingHeader() {
            const routesWithFloatingHeader = ['rent-out'];

            return routesWithFloatingHeader.includes(this.getRouteBaseName());
        },
        shouldShowOwnerCampaignBanner() {
            const rentOutPageName = 'rent-out';
            const isOnRentOutPage = this.$route.name.includes(rentOutPageName);
            const ownerBannerPosthogKey = '2025-07-owner-banner';

            if (!isOnRentOutPage || !['at', 'de'].includes(this.$i18n.locale)) {
                return false;
            }

            if (!this.isFeatureFlagLoaded(ownerBannerPosthogKey)) {
                return false;
            }

            return this.isExperimentEnabled(ownerBannerPosthogKey);
        },
        shouldShowRenterCampaignBanner() {
            const homePageName = 'index';
            const cmsPageName = 'all'; // Search page and brands overview page
            const renterBannerPosthogKey = '2025-07-renter-banner';

            const shouldShowRenterBannerOnThisPage = [homePageName, cmsPageName].some(pageName =>
                this.$route.name.includes(pageName)
            );

            if (!shouldShowRenterBannerOnThisPage || this.isOwner) {
                return false;
            }

            if (!this.isFeatureFlagLoaded(renterBannerPosthogKey)) {
                return false;
            }

            return this.isExperimentEnabled(renterBannerPosthogKey);
        },
        whiteBg() {
            const routesWithWhiteBg = ['booking'];

            return routesWithWhiteBg.includes(this.getRouteBaseName());
        },
        displayFooter() {
            if (this.$nuxt.context.app.nuxt.err) {
                return true;
            }

            const routesWithoutFooter = ['all', 'messages'];

            return !routesWithoutFooter.includes(this.getRouteBaseName());
        },
        displayHeaderWithinErrorPage() {
            return this.$nuxt?.nuxt?.err?.statusCode !== 503; // maintenance mode
        },
    },
    watch: {
        $route: {
            handler() {
                this.$store.dispatch(navigationActions.CLOSE_SUB_MENU);
            },
            immediate: true,
        },
        featureFlagsLoaded: {
            handler(isLoaded) {
                if (isLoaded) {
                    this.handleBannerVisibility();
                }
            },
            immediate: true,
        },
    },
    mounted() {
        this.$store.dispatch(configActions.HEALTH_CHECK);

        this.checkScreenSize();
        window.addEventListener('resize', this.checkScreenSize);
    },
    destroyed() {
        window.removeEventListener('resize', this.checkScreenSize);
    },
    methods: {
        checkScreenSize() {
            const isLargeScreen = window.innerWidth > 991;
            this.$store.dispatch(configActions.SET_IS_LARGE_SCREEN, isLargeScreen);
        },
        /**
         * Triggers after posthog flags get loaded and shows the floating banner after a 2 second delay.
         */
        handleBannerVisibility() {
            if (!this.shouldShowOwnerCampaignBanner && !this.shouldShowRenterCampaignBanner) {
                return;
            }

            setTimeout(() => {
                this.bannerDelayPassed = true;
            }, 2000);
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.main-layout {
    min-height: 100vh;

    &__content-wrapper {
        display: flex;
        flex-direction: column;
    }

    &__content {
        flex-grow: 1;
        position: relative;
    }

    &--floating-header {
        .main-layout__content {
            padding: 0;
        }
    }

    &--white-bg {
        background: $white;
    }
}
</style>
